import {
  salesOrderPrintTemplateOptions,
  salesOrderPrintTemplates,
} from '@/lib/services/sales-orders/sales-order-print-templates'
import type { SalesOrderPrintTemplate } from '@/lib/services/snapdragon/snapdragon.types'
import { readProperty } from '@/lib/utils'

export const superTicketTemplateStorageKey = 'sales-order.super-ticket-template'
export const superTicketPreprintedBrandingStorageKey =
  'sales-order.super-ticket-preprinted-branding-by-template'

/** Whether a super ticket template's paper already carries the shop's branding, by template. */
export type PreprintedBrandingByTemplate = Partial<Record<SalesOrderPrintTemplate, boolean>>

export interface SuperTicketPrintPreferences {
  template: SalesOrderPrintTemplate
  paperHasPreprintedBranding: boolean
}

const defaultSuperTicketTemplate: SalesOrderPrintTemplate =
  salesOrderPrintTemplates.JhcFb7373SuperTicket
const superTicketOptions = salesOrderPrintTemplateOptions.filter(
  ({ format }) => format === 'super_ticket'
)
const superTicketOptionsByValue = new Map(
  superTicketOptions.map((option) => [option.value, option])
)

function isSuperTicketTemplate(template: unknown): template is SalesOrderPrintTemplate {
  return superTicketOptions.some((option) => option.value === template)
}

function supportedTemplate(template: unknown): SalesOrderPrintTemplate {
  return isSuperTicketTemplate(template) ? template : defaultSuperTicketTemplate
}

function storedSuperTicketTemplate(): SalesOrderPrintTemplate | null {
  if (typeof window === 'undefined') return null

  try {
    const template = window.localStorage.getItem(superTicketTemplateStorageKey)
    return isSuperTicketTemplate(template) ? template : null
  } catch {
    return null
  }
}

export function getStoredSuperTicketTemplate(
  fallbackTemplate: string = defaultSuperTicketTemplate
): SalesOrderPrintTemplate {
  const fallback = supportedTemplate(fallbackTemplate)
  return storedSuperTicketTemplate() ?? fallback
}

export function rememberSuperTicketTemplate(template: string): void {
  if (typeof window === 'undefined' || !isSuperTicketTemplate(template)) return

  try {
    window.localStorage.setItem(superTicketTemplateStorageKey, template)
  } catch {
    // A blocked localStorage preference should not prevent printing.
  }
}

export function getStoredPreprintedBrandingByTemplate(): PreprintedBrandingByTemplate {
  if (typeof window === 'undefined') return {}

  try {
    const storedPreferences: unknown = JSON.parse(
      window.localStorage.getItem(superTicketPreprintedBrandingStorageKey) ?? '{}'
    )

    if (
      !storedPreferences ||
      typeof storedPreferences !== 'object' ||
      Array.isArray(storedPreferences)
    ) {
      return {}
    }

    const preferences: PreprintedBrandingByTemplate = {}
    for (const { supportsPreprintedBranding, value } of superTicketOptions) {
      const preference = readProperty(storedPreferences, value)
      if (supportsPreprintedBranding && typeof preference === 'boolean') {
        preferences[value] = preference
      }
    }
    return preferences
  } catch {
    return {}
  }
}

export function rememberPreprintedBrandingByTemplate(
  preferences: PreprintedBrandingByTemplate
): void {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      superTicketPreprintedBrandingStorageKey,
      JSON.stringify(preferences)
    )
  } catch {
    // A blocked localStorage preference should not prevent printing.
  }
}

export function getSuperTicketPrintPreferences({
  fallbackTemplate = defaultSuperTicketTemplate,
  fallbackPaperHasPreprintedBranding = false,
}: {
  fallbackTemplate?: string
  fallbackPaperHasPreprintedBranding?: boolean
} = {}): SuperTicketPrintPreferences {
  const storedTemplate = storedSuperTicketTemplate()
  const template = storedTemplate ?? supportedTemplate(fallbackTemplate)
  const option = superTicketOptionsByValue.get(template)
  const storedBrandingPreferences = getStoredPreprintedBrandingByTemplate()
  const useFallbackBranding =
    storedTemplate === null &&
    template === supportedTemplate(fallbackTemplate) &&
    typeof storedBrandingPreferences[template] !== 'boolean'

  return {
    template,
    paperHasPreprintedBranding:
      option?.supportsPreprintedBranding === true &&
      (useFallbackBranding
        ? fallbackPaperHasPreprintedBranding === true
        : storedBrandingPreferences[template] === true),
  }
}
